import React from 'lib/react';
import PropTypes from 'prop-types';
import { EVGrid, makeStyles } from 'lib/components';
import AnalyticsBarChart from 'commonComponents/analyticsBarChart';
import { ChartsReferenceLine } from '@mui/x-charts';
import {
  getCurrentMonthName,
  padHistoricUsageTo12Months,
  formatChartValue,
  formatMonthName,
} from '../Consumption.utils';
import HistoricUsageTooltip from './HistoricUsageTooltip';

// #genai
const useStyles = makeStyles((theme) => ({
  historicUsageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.evPalette.salt[100],
  },
  barChartDiv: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '320px',
    borderLeft: `1px solid ${theme.evPalette.charcoal[400]}`,
    borderRight: `1px solid ${theme.evPalette.charcoal[400]}`,
    borderBottom: `1px solid ${theme.evPalette.charcoal[400]}`,
    borderRadius: '4px',
    '& > *': {
      flex: 1,
      minHeight: 0,
    },
  },
}));

function OveragePattern() {
  return (
    <defs>
      <pattern id="overage-stripe" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
        <rect width="4" height="8" fill="#E9B02D" />
        <rect x="4" width="4" height="8" fill="#D4A01F" />
      </pattern>
    </defs>
  );
}

function InProcessPattern() {
  return (
    <defs>
      <pattern id="inprocess-stripe" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
        <rect width="4" height="8" fill="#3E649B" opacity="0.4" />
        <rect x="4" width="4" height="8" fill="#E8EBF0" />
      </pattern>
    </defs>
  );
}

function HistoricUsage({ historicUsage, totalAllowed }) {
  const classes = useStyles();
  const currentMonth = getCurrentMonthName();

  const getFormattedHistoricUsageData = () => {
    const paddedData = padHistoricUsageTo12Months(historicUsage);
    const hasData = historicUsage && historicUsage.length > 0;
    return paddedData.map((item) => {
      const isCurrentMonth = item.month === currentMonth;
      const isNotStarted = item.notStarted || false;
      const qty = item.qty || 0;
      const usedBelow = qty < totalAllowed ? qty : totalAllowed;
      const overage = Math.max(0, qty - totalAllowed);

      return {
        ...item,
        // Not started months: show small grey bar only if we have actual data
        notStartedBar: (hasData && isNotStarted) ? 0.1 : 0,
        // Past months: show in regular series (solid colors)
        usedBelow: isCurrentMonth || isNotStarted ? 0 : usedBelow,
        overage: isCurrentMonth || isNotStarted ? 0 : overage,
        // Current month: show in in-process series (striped patterns)
        usedBelowInProcess: isCurrentMonth ? usedBelow : 0,
        overageInProcess: isCurrentMonth ? overage : 0,
      };
    });
  };

  const historicUsageBarChartSeries = [
    {
      dataKey: 'notStartedBar',
      color: '#D1D5DB',
      label: 'Not Started',
      valueFormatter: () => null,
      stack: 'usage',
    },
    {
      dataKey: 'usedBelow',
      color: '#3E649B',
      label: 'Used',
      valueFormatter: formatChartValue,
      stack: 'usage',
    },
    {
      dataKey: 'overage',
      color: 'url(#overage-stripe)',
      label: 'Overage',
      valueFormatter: formatChartValue,
      stack: 'usage',
    },
    {
      dataKey: 'usedBelowInProcess',
      color: 'url(#inprocess-stripe)',
      label: 'Used',
      valueFormatter: formatChartValue,
      stack: 'usage',
    },
    {
      dataKey: 'overageInProcess',
      color: 'url(#overage-stripe)',
      label: 'Overage',
      valueFormatter: formatChartValue,
      stack: 'usage',
    },
  ];

  return (
    <EVGrid className={classes.historicUsageContainer}>
      <EVGrid className={classes.barChartDiv}>
        <AnalyticsBarChart
          dataset={getFormattedHistoricUsageData()}
          series={historicUsageBarChartSeries}
          margin={{
            top: 0, right: 0, bottom: 24, left: 0,
          }}
          sx={{
            width: '100%',
            height: '100%',
            '& .MuiChartsGrid-lineVertical:not(:first-of-type):not(:last-of-type)': {
              stroke: 'none',
            },
            '& .MuiChartsAxis-root': {
              stroke: 'none',
            },
            '& .MuiChartsGrid-line': {
              stroke: '#B4BAC7',
            },
          }}
          tooltip={{ trigger: 'axis' }}
          slots={{
            axisContent: () => null,
          }}
          xAxis={[{
            scaleType: 'band',
            dataKey: 'month',
            categoryGapRatio: 0.2,
            disableTicks: true,
            valueFormatter: formatMonthName,
          }]}
          bargapratio={0}
          leftAxis={null}
          grid={{ vertical: false }}
        >
          <OveragePattern />
          <InProcessPattern />
          <HistoricUsageTooltip series={historicUsageBarChartSeries} />
          <ChartsReferenceLine
            y={totalAllowed}
            lineStyle={{
              stroke: '#6B7280',
              strokeDasharray: '5 5',
              strokeWidth: 1,
            }}
          />
        </AnalyticsBarChart>
      </EVGrid>
    </EVGrid>
  );
}

HistoricUsage.propTypes = {
  historicUsage: PropTypes.arrayOf(PropTypes.shape({
    month: PropTypes.string.isRequired,
    qty: PropTypes.number.isRequired,
  })).isRequired,
  totalAllowed: PropTypes.number.isRequired,
};

export default HistoricUsage;
